<template>
  <div class="pa-0 ma-0 fill-height monitoring-administration" fluid>
    <v-dialog
      v-model="overlay"
      hide-overlay
      persistent
      width="300"
      lazy
    >
      <v-card color="secondary" dark>
        <v-card-text>
          {{ overlayMessage }}
          <v-progress-linear
            indeterminate
            color="white"
            class="mb-0"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="threadDialogOpen"
      max-width="960"
      scrollable
      @input="onThreadDialogToggle"
    >
      <v-card>
        <v-card-title class="py-3 d-flex align-center">
          <v-icon left color="primary">mdi-forum-outline</v-icon>
          <span class="text-truncate">{{ selectedThreadSummary.title || "Tracey Thread" }}</span>
          <v-spacer />
          <v-btn icon @click="closeThreadDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text class="pa-4">
          <div v-if="loadingThreadContents" class="text-center py-8">
            <v-progress-circular indeterminate color="primary" />
          </div>

          <v-alert
            v-else-if="threadContentsError"
            type="error"
            outlined
            dense
          >
            {{ threadContentsError }}
          </v-alert>

          <template v-else>
            <v-card outlined class="mb-4">
              <v-card-text class="py-3">
                <v-row dense>
                  <v-col cols="12" md="6">
                    <div class="caption grey--text text--darken-1">User</div>
                    <div class="body-2 font-weight-medium">
                      {{ selectedThreadSummary.full_name || "-" }}
                    </div>
                    <div class="body-2">{{ selectedThreadSummary.email || "-" }}</div>
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="caption grey--text text--darken-1">Thread ID</div>
                    <div class="body-2">{{ selectedThreadSummary.thread_id || "-" }}</div>
                  </v-col>
                  <v-col cols="12" md="4">
                    <div class="caption grey--text text--darken-1">Status</div>
                    <v-chip
                      small
                      :color="getThreadStatusColor(selectedThreadSummary.status)"
                      text-color="white"
                      class="mt-1"
                    >
                      {{ selectedThreadSummary.status || "-" }}
                    </v-chip>
                  </v-col>
                  <v-col cols="12" md="4">
                    <div class="caption grey--text text--darken-1">Created</div>
                    <div class="body-2">{{ selectedThreadSummary.created_display || formatDate(selectedThreadSummary.created) }}</div>
                  </v-col>
                  <v-col cols="12" md="4">
                    <div class="caption grey--text text--darken-1">Updated</div>
                    <div class="body-2">{{ selectedThreadSummary.updated_display || formatDate(selectedThreadSummary.updated) }}</div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <div class="thread-messages">
              <article
                v-for="content in displayedThreadContents"
                :key="content.id"
                class="ai-turn"
                :class="`ai-turn--${content.role}`"
              >
                <div
                  class="ai-turn__avatar"
                  :class="{ 'ai-turn__avatar--tracey': content.role === 'assistant' }"
                >
                  <v-icon v-if="content.role === 'assistant'" small color="#fff">
                    mdi-creation
                  </v-icon>
                  <span v-else class="ai-turn__avatar-letter">
                    {{ getThreadUserInitial(content) }}
                  </span>
                </div>

                <div class="ai-turn__body">
                  <header v-if="content.created_display" class="ai-turn__meta">
                    {{ content.created_display }}
                  </header>

                  <div v-if="content.role === 'user'" class="ai-turn__text">
                    {{ content.content || "-" }}
                  </div>
                  <div
                    v-else
                    class="ai-turn__text ai-prose"
                    v-html="parseAssistantContent(content.content)"
                  />

                  <div
                    v-if="content.role === 'assistant' && content.toolNames.length"
                    class="thread-message-tools"
                  >
                    <div class="thread-message-tools__label">Tools Used</div>
                    <div class="d-flex flex-wrap">
                      <v-chip
                        v-for="toolName in content.toolNames"
                        :key="`${content.id}-${toolName}`"
                        small
                        outlined
                        class="mr-2 mb-1"
                      >
                        {{ toolName }}
                      </v-chip>
                    </div>
                  </div>
                </div>
              </article>
            </div>

            <div
              v-if="!loadingThreadContents && !displayedThreadContents.length"
              class="body-2 grey--text text--darken-1 text-center py-6"
            >
              No user or assistant messages found for this thread.
            </div>

            <div
              v-if="totalThreadContentsPages > 1"
              class="d-flex justify-center mt-4"
            >
              <v-pagination
                v-model="threadContentsPage"
                :length="Math.max(totalThreadContentsPages, 1)"
                :total-visible="5"
                color="primary"
                prev-icon="mdi-menu-left"
                next-icon="mdi-menu-right"
                @input="fetchThreadContents"
              />
            </div>
          </template>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      :value="!!downloadingAll"
      persistent
      width="360"
    >
      <v-card>
        <v-card-title class="py-3">
          <v-icon left color="primary">mdi-download</v-icon>
          Download All
        </v-card-title>
        <v-card-text>
          {{ downloadAllMessage }}
          <v-progress-linear
            class="mt-3"
            color="primary"
            height="8"
            :value="downloadAllProgress"
          />
          <div class="caption mt-1 text-right">{{ downloadAllProgress }}%</div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <div v-if="!overlay">
      <v-row justify="center" class="px-0 py-0">
        <v-col cols="12">
          <v-card color="#f5f5f5" flat tile max-width="1400" class="mx-auto">
            <!-- <p
              style="text-align:center;"
              class="pt-6 display-1 font-weight-medium"
            >
              Monitoring
            </p>
            <p style="text-align:center;" class="body-1 font-weight-normal">
              Monitor admin-level activity and search records across the platform.
            </p> -->

            <v-row v-if="error" class="mx-3">
              <v-col cols="12">
                <v-alert type="error" dismissible @input="error = null">
                  <strong>Error:</strong> {{ error }}
                </v-alert>
              </v-col>
            </v-row>

            <v-row v-else-if="!isAdmin" class="mx-3">
              <v-col cols="12">
                <v-alert type="warning" outlined>
                  Admin access is required to use monitoring.
                </v-alert>
              </v-col>
            </v-row>

            <div v-else class="mx-3 mb-6">
              <v-tabs
                v-model="activeTab"
                background-color="transparent"
                color="primary"
                class="mb-4"
              >
                <v-tab>Searches</v-tab>
                <v-tab>User History</v-tab>
                <v-tab>User Search Stats</v-tab>
                <v-tab>Tracey Threads</v-tab>
              </v-tabs>

              <v-tabs-items v-model="activeTab" class="transparent-tabs">
                <v-tab-item>
                  <v-card elevation="1">
                    <v-card-title class="py-3">
                      <v-icon left color="primary">mdi-magnify</v-icon>
                      Search Activity
                    </v-card-title>
                    <v-card-text class="pt-0">
                      <v-row>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="filters.full_name"
                            label="Filter by full name"
                            outlined
                            dense
                            clearable
                            @keyup.enter="runSearch"
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="filters.email"
                            label="Filter by email"
                            outlined
                            dense
                            clearable
                            @keyup.enter="runSearch"
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="filters.plain_text_query"
                            label="Filter by plain-text query"
                            outlined
                            dense
                            clearable
                            @keyup.enter="runSearch"
                          />
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="filters.run_date_from"
                            label="Run date from"
                            type="date"
                            outlined
                            dense
                            clearable
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="filters.run_date_to"
                            label="Run date to"
                            type="date"
                            outlined
                            dense
                            clearable
                          />
                        </v-col>
                        <v-col cols="12" md="4" class="d-flex">
                          <v-btn
                            text
                            color="secondary"
                            class="mr-2"
                            @click="clearFilters"
                          >
                            Clear Filters
                          </v-btn>
                          <v-btn
                            color="primary"
                            outlined
                            :loading="loadingSearches"
                            @click="runSearch"
                          >
                            Apply Filters
                          </v-btn>
                        </v-col>
                      </v-row>

                      <v-data-table
                        :headers="searchHeaders"
                        :items="searches"
                        :loading="loadingSearches"
                        :items-per-page="pageSize"
                        :expanded.sync="expandedSearchRows"
                        show-expand
                        hide-default-footer
                        item-key="id"
                        class="elevation-1"
                      >
                        <template slot="item.full_name" slot-scope="{ item }">
                          <div class="font-weight-medium">{{ item.full_name || "-" }}</div>
                        </template>

                        <template slot="item.email" slot-scope="{ item }">
                          <span>{{ item.email || "-" }}</span>
                        </template>

                        <template slot="item.plain_text_query" slot-scope="{ item }">
                          <button
                            type="button"
                            class="monitoring-query-button"
                            @click="toggleSearchExpansion(item)"
                          >
                            <span class="monitoring-query-button__title">
                              {{ getSearchQueryTitle(item) }}
                            </span>
                            <span
                              v-if="getSearchQuerySubtitle(item)"
                              class="monitoring-query-button__subtitle"
                            >
                              {{ shortenText(getSearchQuerySubtitle(item), 90) }}
                            </span>
                          </button>
                        </template>

                        <template v-slot:item.run_date_display="{ item }">
                          {{ item.run_date_display }}
                        </template>

                        <template slot="item.is_saved" slot-scope="{ item }">
                          <v-chip
                            small
                            :color="item.is_saved ? 'success' : 'grey'"
                            text-color="white"
                          >
                            {{ item.is_saved ? "Saved" : "No" }}
                          </v-chip>
                        </template>

                        <template slot="item.alerts_on" slot-scope="{ item }">
                          <v-chip
                            small
                            :color="item.alerts_on ? 'success' : 'grey'"
                            text-color="white"
                          >
                            {{ item.alerts_on ? "On" : "Off" }}
                          </v-chip>
                        </template>

                        <template slot="expanded-item" slot-scope="{ headers, item }">
                          <td :colspan="headers.length" class="pa-4 grey lighten-5">
                            <div class="body-2">
                              <strong>Full Query:</strong> {{ item.plain_text_query || "-" }}
                            </div>
                          </td>
                        </template>
                      </v-data-table>

                      <div class="d-flex flex-column flex-md-row align-center justify-space-between mt-4">
                        <div class="d-flex align-center flex-wrap mb-3 mb-md-0">
                          <div class="caption grey--text text--darken-1 mr-3">
                            Showing {{ searches.length }} of {{ totalSearches }} searches.
                          </div>
                          <v-btn
                            small
                            text
                            color="primary"
                            :disabled="!searches.length || loadingSearches || !!downloadingAll"
                            @click="downloadSearchesCsv"
                          >
                            <v-icon left small>mdi-download</v-icon>
                            Download CSV
                          </v-btn>
                          <v-btn
                            small
                            text
                            color="primary"
                            :disabled="!totalSearches || loadingSearches || !!downloadingAll"
                            :loading="downloadingAll === 'searches'"
                            @click="downloadAllSearchesCsv"
                          >
                            <v-icon left small>mdi-download-multiple</v-icon>
                            Download All
                          </v-btn>
                        </div>

                        <v-pagination
                          v-model="currentPage"
                          :length="Math.max(totalPages, 1)"
                          :total-visible="7"
                          color="primary"
                          prev-icon="mdi-menu-left"
                          next-icon="mdi-menu-right"
                          @input="fetchSearches"
                        />
                      </div>
                    </v-card-text>
                  </v-card>
                </v-tab-item>

                <v-tab-item>
                  <v-card elevation="1">
                    <v-card-title class="py-3">
                      <v-icon left color="primary">mdi-history</v-icon>
                      User History
                    </v-card-title>
                    <v-card-text class="pt-0">
                      <v-row>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="userHistoryFilters.full_name"
                            label="Filter by full name"
                            outlined
                            dense
                            clearable
                            @keyup.enter="runUserHistorySearch"
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="userHistoryFilters.email"
                            label="Filter by email"
                            outlined
                            dense
                            clearable
                            @keyup.enter="runUserHistorySearch"
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="userHistoryFilters.route_name"
                            label="Filter by route name"
                            outlined
                            dense
                            clearable
                            @keyup.enter="runUserHistorySearch"
                          />
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="userHistoryFilters.path"
                            label="Filter by path"
                            outlined
                            dense
                            clearable
                            @keyup.enter="runUserHistorySearch"
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="userHistoryFilters.url"
                            label="Filter by URL"
                            outlined
                            dense
                            clearable
                            @keyup.enter="runUserHistorySearch"
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="userHistoryFilters.description"
                            label="Filter by description"
                            outlined
                            dense
                            clearable
                            @keyup.enter="runUserHistorySearch"
                          />
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="userHistoryFilters.created_from"
                            label="Created from"
                            type="date"
                            outlined
                            dense
                            clearable
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="userHistoryFilters.created_to"
                            label="Created to"
                            type="date"
                            outlined
                            dense
                            clearable
                          />
                        </v-col>
                        <v-col cols="12" md="4" class="d-flex">
                          <v-btn
                            text
                            color="secondary"
                            class="mr-2"
                            @click="clearUserHistoryFilters"
                          >
                            Clear Filters
                          </v-btn>
                          <v-btn
                            color="primary"
                            outlined
                            :loading="loadingUserHistory"
                            @click="runUserHistorySearch"
                          >
                            Apply Filters
                          </v-btn>
                        </v-col>
                      </v-row>

                      <v-data-table
                        :headers="userHistoryHeaders"
                        :items="userHistory"
                        :loading="loadingUserHistory"
                        :items-per-page="userHistoryPageSize"
                        show-expand
                        hide-default-footer
                        item-key="id"
                        class="elevation-1"
                      >
                        <template slot="item.full_name" slot-scope="{ item }">
                          <div class="font-weight-medium">{{ item.full_name || "-" }}</div>
                        </template>

                        <template slot="item.email" slot-scope="{ item }">
                          <span>{{ item.email || "-" }}</span>
                        </template>

                        <template slot="item.created" slot-scope="{ item }">
                          {{ formatDate(item.created) }}
                        </template>

                        <template slot="item.path" slot-scope="{ item }">
                          <span>{{ shortenText(item.path, 80) }}</span>
                        </template>

                        <template slot="item.description" slot-scope="{ item }">
                          <div class="monitoring-description">
                            <div class="monitoring-description__title">
                              {{ getHistoryDescriptionTitle(item) }}
                            </div>
                            <div
                              v-if="getHistoryDescriptionSubtitle(item)"
                              class="monitoring-description__subtitle"
                            >
                              {{ shortenText(getHistoryDescriptionSubtitle(item), 90) }}
                            </div>
                          </div>
                        </template>

                        <template slot="item.url" slot-scope="{ item }">
                          <a
                            v-if="item.url"
                            :href="item.url"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="monitoring-link"
                          >
                            {{ shortenText(item.url, 100) }}
                          </a>
                          <span v-else>-</span>
                        </template>

                        <template slot="expanded-item" slot-scope="{ headers, item }">
                          <td :colspan="headers.length" class="pa-4 grey lighten-5">
                            <div class="body-2 mb-2">
                              <strong>Parameter:</strong> {{ item.parameter || "-" }}
                            </div>
                            <div class="body-2 mb-2">
                              <strong>Query Params:</strong> {{ formatQueryStringParams(item.query_string_params) }}
                            </div>
                            <div class="body-2">
                              <strong>Description:</strong> {{ item.description || "-" }}
                            </div>
                          </td>
                        </template>
                      </v-data-table>

                      <div class="d-flex flex-column flex-md-row align-center justify-space-between mt-4">
                        <div class="d-flex align-center flex-wrap mb-3 mb-md-0">
                          <div class="caption grey--text text--darken-1 mr-3">
                            Showing {{ userHistory.length }} of {{ totalHistory }} history records.
                          </div>
                          <v-btn
                            small
                            text
                            color="primary"
                            :disabled="!userHistory.length || loadingUserHistory || !!downloadingAll"
                            @click="downloadUserHistoryCsv"
                          >
                            <v-icon left small>mdi-download</v-icon>
                            Download CSV
                          </v-btn>
                          <v-btn
                            small
                            text
                            color="primary"
                            :disabled="!totalHistory || loadingUserHistory || !!downloadingAll"
                            :loading="downloadingAll === 'user_history'"
                            @click="downloadAllUserHistoryCsv"
                          >
                            <v-icon left small>mdi-download-multiple</v-icon>
                            Download All
                          </v-btn>
                        </div>

                        <v-pagination
                          v-model="userHistoryPage"
                          :length="Math.max(totalHistoryPages, 1)"
                          :total-visible="7"
                          color="primary"
                          prev-icon="mdi-menu-left"
                          next-icon="mdi-menu-right"
                          @input="fetchUserHistory"
                        />
                      </div>
                    </v-card-text>
                  </v-card>
                </v-tab-item>

                <v-tab-item>
                  <v-card elevation="1">
                    <v-card-title class="py-3">
                      <v-icon left color="primary">mdi-chart-box</v-icon>
                      User Search Stats
                    </v-card-title>
                    <v-card-text class="pt-0">
                      <v-row>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="searchStatsFilters.full_name"
                            label="Filter by full name"
                            outlined
                            dense
                            clearable
                            @keyup.enter="runSearchStatsSearch"
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="searchStatsFilters.email"
                            label="Filter by email"
                            outlined
                            dense
                            clearable
                            @keyup.enter="runSearchStatsSearch"
                          />
                        </v-col>
                        <v-col cols="12" md="4" class="d-flex">
                          <v-btn
                            text
                            color="secondary"
                            class="mr-2"
                            @click="clearSearchStatsFilters"
                          >
                            Clear Filters
                          </v-btn>
                          <v-btn
                            color="primary"
                            outlined
                            :loading="loadingSearchStats"
                            @click="runSearchStatsSearch"
                          >
                            Apply Filters
                          </v-btn>
                        </v-col>
                      </v-row>

                      <v-data-table
                        :headers="searchStatsHeaders"
                        :items="searchStats"
                        :loading="loadingSearchStats"
                        :items-per-page="searchStatsPageSize"
                        hide-default-footer
                        item-key="email"
                        class="elevation-1"
                      >
                        <template slot="item.full_name" slot-scope="{ item }">
                          <div class="font-weight-medium">{{ item.full_name || "-" }}</div>
                        </template>

                        <template slot="item.email" slot-scope="{ item }">
                          <span>{{ item.email || "-" }}</span>
                        </template>

                        <template slot="item.first_search_date" slot-scope="{ item }">
                          {{ formatDate(item.first_search_date) }}
                        </template>

                        <template slot="item.last_new_search_date" slot-scope="{ item }">
                          {{ formatDate(item.last_new_search_date) }}
                        </template>

                        <template slot="item.last_search_date" slot-scope="{ item }">
                          {{ formatDate(item.last_search_date) }}
                        </template>
                      </v-data-table>

                      <div class="d-flex flex-column flex-md-row align-center justify-space-between mt-4">
                        <div class="d-flex align-center flex-wrap mb-3 mb-md-0">
                          <div class="caption grey--text text--darken-1 mr-3">
                            Showing {{ searchStats.length }} of {{ totalSearchStats }} user stats records.
                          </div>
                          <v-btn
                            small
                            text
                            color="primary"
                            :disabled="!searchStats.length || loadingSearchStats || !!downloadingAll"
                            @click="downloadSearchStatsCsv"
                          >
                            <v-icon left small>mdi-download</v-icon>
                            Download CSV
                          </v-btn>
                          <v-btn
                            small
                            text
                            color="primary"
                            :disabled="!totalSearchStats || loadingSearchStats || !!downloadingAll"
                            :loading="downloadingAll === 'search_stats'"
                            @click="downloadAllSearchStatsCsv"
                          >
                            <v-icon left small>mdi-download-multiple</v-icon>
                            Download All
                          </v-btn>
                        </div>

                        <v-pagination
                          v-model="searchStatsPage"
                          :length="Math.max(totalSearchStatsPages, 1)"
                          :total-visible="7"
                          color="primary"
                          prev-icon="mdi-menu-left"
                          next-icon="mdi-menu-right"
                          @input="fetchSearchStats"
                        />
                      </div>
                    </v-card-text>
                  </v-card>
                </v-tab-item>

                <v-tab-item>
                  <v-card elevation="1">
                    <v-card-title class="py-3">
                      <v-icon left color="primary">mdi-forum-outline</v-icon>
                      Tracey Threads
                    </v-card-title>
                    <v-card-text class="pt-0">
                      <v-row>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="traceyThreadFilters.full_name"
                            label="Filter by full name"
                            outlined
                            dense
                            clearable
                            @keyup.enter="runTraceyThreadSearch"
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="traceyThreadFilters.email"
                            label="Filter by email"
                            outlined
                            dense
                            clearable
                            @keyup.enter="runTraceyThreadSearch"
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="traceyThreadFilters.title"
                            label="Filter by title"
                            outlined
                            dense
                            clearable
                            @keyup.enter="runTraceyThreadSearch"
                          />
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="traceyThreadFilters.status"
                            label="Filter by status"
                            outlined
                            dense
                            clearable
                            hint="e.g. active, deleted"
                            persistent-hint
                            @keyup.enter="runTraceyThreadSearch"
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="traceyThreadFilters.created_from"
                            label="Created from"
                            type="date"
                            outlined
                            dense
                            clearable
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="traceyThreadFilters.created_to"
                            label="Created to"
                            type="date"
                            outlined
                            dense
                            clearable
                          />
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="traceyThreadFilters.updated_from"
                            label="Updated from"
                            type="date"
                            outlined
                            dense
                            clearable
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="traceyThreadFilters.updated_to"
                            label="Updated to"
                            type="date"
                            outlined
                            dense
                            clearable
                          />
                        </v-col>
                        <v-col cols="12" md="4" class="d-flex align-end">
                          <v-btn
                            text
                            color="secondary"
                            class="mr-2"
                            @click="clearTraceyThreadFilters"
                          >
                            Clear Filters
                          </v-btn>
                          <v-btn
                            color="primary"
                            outlined
                            :loading="loadingTraceyThreads"
                            @click="runTraceyThreadSearch"
                          >
                            Apply Filters
                          </v-btn>
                        </v-col>
                      </v-row>

                      <v-data-table
                        :headers="traceyThreadHeaders"
                        :items="traceyThreads"
                        :loading="loadingTraceyThreads"
                        :items-per-page="traceyThreadsPageSize"
                        hide-default-footer
                        item-key="id"
                        class="elevation-1"
                      >
                        <template slot="item.full_name" slot-scope="{ item }">
                          <div class="font-weight-medium">{{ item.full_name || "-" }}</div>
                        </template>

                        <template slot="item.email" slot-scope="{ item }">
                          <span>{{ item.email || "-" }}</span>
                        </template>

                        <template slot="item.title" slot-scope="{ item }">
                          <button
                            type="button"
                            class="monitoring-query-button"
                            @click="openThreadDialog(item)"
                          >
                            <span class="monitoring-query-button__title">
                              {{ item.title || "Untitled thread" }}
                            </span>
                            <span class="monitoring-query-button__subtitle">
                              {{ shortenText(item.thread_id, 40) }}
                            </span>
                          </button>
                        </template>

                        <template slot="item.status" slot-scope="{ item }">
                          <v-chip
                            small
                            :color="getThreadStatusColor(item.status)"
                            text-color="white"
                          >
                            {{ item.status || "-" }}
                          </v-chip>
                        </template>

                        <template v-slot:item.created_display="{ item }">
                          {{ item.created_display }}
                        </template>

                        <template v-slot:item.updated_display="{ item }">
                          {{ item.updated_display }}
                        </template>

                        <template slot="item.actions" slot-scope="{ item }">
                          <v-btn
                            small
                            text
                            color="primary"
                            @click="openThreadDialog(item)"
                          >
                            View
                          </v-btn>
                        </template>
                      </v-data-table>

                      <div class="d-flex flex-column flex-md-row align-center justify-space-between mt-4">
                        <div class="d-flex align-center flex-wrap mb-3 mb-md-0">
                          <div class="caption grey--text text--darken-1 mr-3">
                            Showing {{ traceyThreads.length }} of {{ totalTraceyThreads }} threads.
                          </div>
                          <v-btn
                            small
                            text
                            color="primary"
                            :disabled="!traceyThreads.length || loadingTraceyThreads || !!downloadingAll"
                            @click="downloadTraceyThreadsCsv"
                          >
                            <v-icon left small>mdi-download</v-icon>
                            Download CSV
                          </v-btn>
                          <v-btn
                            small
                            text
                            color="primary"
                            :disabled="!totalTraceyThreads || loadingTraceyThreads || !!downloadingAll"
                            :loading="downloadingAll === 'tracey_threads'"
                            @click="downloadAllTraceyThreadsCsv"
                          >
                            <v-icon left small>mdi-download-multiple</v-icon>
                            Download All
                          </v-btn>
                        </div>

                        <v-pagination
                          v-model="traceyThreadsPage"
                          :length="Math.max(totalTraceyThreadsPages, 1)"
                          :total-visible="7"
                          color="primary"
                          prev-icon="mdi-menu-left"
                          next-icon="mdi-menu-right"
                          @input="fetchTraceyThreads"
                        />
                      </div>
                    </v-card-text>
                  </v-card>
                </v-tab-item>
              </v-tabs-items>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import { getCurrentUserProfile } from "@/components/APIManager.js";
import {
  getAllSearches,
  getSearchStatsByUser,
  getAllUserHistory,
  getAllAIConversationThreads,
  getAIConversationContents,
} from "@/components/AdminAPIManager.js";
import { convertToCSV, exportCSVFile } from "@/components/Utils.js";
import { parseAssistantMarkdown } from "@/components/tracey/page/markdownUtils.js";
import { formatAdminDateTime, withAdminFormattedDates } from "@/utils/adminDateTime.js";

function extractToolCallNames(toolCalls) {
  if (!Array.isArray(toolCalls) || !toolCalls.length) {
    return [];
  }

  const names = toolCalls
    .map((toolCall) => {
      if (typeof toolCall === "string") {
        return toolCall.trim();
      }

      if (toolCall && toolCall.name) {
        return String(toolCall.name).trim();
      }

      if (toolCall && toolCall.function && toolCall.function.name) {
        return String(toolCall.function.name).trim();
      }

      return "";
    })
    .filter(Boolean);

  return [...new Set(names)];
}

export default {
  name: "MonitoringAdministration",

  data() {
    return {
      overlay: true,
      overlayMessage: "Loading monitoring...",
      error: null,
      isAdmin: false,
      activeTab: 0,
      hasLoadedUserHistory: false,
      hasLoadedSearchStats: false,
      hasLoadedTraceyThreads: false,
      downloadingAll: null,
      downloadAllMessage: "",
      downloadAllProgress: 0,
      loadingSearches: false,
      loadingUserHistory: false,
      loadingSearchStats: false,
      loadingTraceyThreads: false,
      loadingThreadContents: false,
      searches: [],
      expandedSearchRows: [],
      userHistory: [],
      searchStats: [],
      traceyThreads: [],
      threadDialogOpen: false,
      selectedThreadId: null,
      selectedThreadSummary: {},
      threadContents: [],
      threadContentsError: null,
      threadContentsPage: 1,
      threadContentsPageSize: 50,
      totalThreadContents: 0,
      totalThreadContentsPages: 1,
      filters: {
        full_name: "",
        email: "",
        plain_text_query: "",
        run_date_from: "",
        run_date_to: "",
      },
      userHistoryFilters: {
        full_name: "",
        email: "",
        url: "",
        path: "",
        route_name: "",
        description: "",
        created_from: "",
        created_to: "",
      },
      searchStatsFilters: {
        full_name: "",
        email: "",
      },
      traceyThreadFilters: {
        full_name: "",
        email: "",
        title: "",
        status: "",
        created_from: "",
        created_to: "",
        updated_from: "",
        updated_to: "",
      },
      currentPage: 1,
      pageSize: 50,
      totalSearches: 0,
      totalPages: 1,
      userHistoryPage: 1,
      userHistoryPageSize: 50,
      totalHistory: 0,
      totalHistoryPages: 1,
      searchStatsPage: 1,
      searchStatsPageSize: 50,
      totalSearchStats: 0,
      totalSearchStatsPages: 1,
      traceyThreadsPage: 1,
      traceyThreadsPageSize: 50,
      totalTraceyThreads: 0,
      totalTraceyThreadsPages: 1,
      searchHeaders: [
        { text: "Full Name", value: "full_name", width: "15%" },
        { text: "Email", value: "email", width: "18%" },
        { text: "Repeats", value: "repeats", width: "8%" },
        { text: "Run Date", value: "run_date_display", width: "14%" },
        { text: "Plain Text Query", value: "plain_text_query", width: "27%" },
        { text: "Saved", value: "is_saved", width: "8%" },
        { text: "Alerts", value: "alerts_on", width: "10%" },
      ],
      userHistoryHeaders: [
        { text: "Full Name", value: "full_name", width: "14%" },
        { text: "Email", value: "email", width: "16%" },
        { text: "Created", value: "created", width: "14%" },
        { text: "Route", value: "route_name", width: "12%" },
        { text: "Path", value: "path", width: "16%" },
        { text: "Description", value: "description", width: "14%" },
        { text: "URL", value: "url", width: "14%" },
      ],
      searchStatsHeaders: [
        { text: "Full Name", value: "full_name", width: "20%" },
        { text: "Email", value: "email", width: "24%" },
        { text: "Unique Searches", value: "unique_searches", width: "4%" },
        { text: "Searches incl. Repeats", value: "num_searches_inc_repeats", width: "5%" },
        { text: "First Search", value: "first_search_date", width: "16%" },
        { text: "Last New Search", value: "last_new_search_date", width: "16%" },
        { text: "Last Search", value: "last_search_date", width: "15%" },
      ],
      traceyThreadHeaders: [
        { text: "Full Name", value: "full_name", width: "14%" },
        { text: "Email", value: "email", width: "16%" },
        { text: "Title", value: "title", width: "24%" },
        { text: "Status", value: "status", width: "10%" },
        { text: "Created", value: "created_display", width: "14%" },
        { text: "Updated", value: "updated_display", width: "14%" },
        { text: "", value: "actions", sortable: false, width: "8%" },
      ],
    };
  },

  computed: {
    displayedThreadContents() {
      return this.threadContents
        .filter((content) => content.role === "user" || content.role === "assistant")
        .map((content) => ({
          ...content,
          toolNames: extractToolCallNames(content.tool_calls),
          created_display: formatAdminDateTime(content.created),
        }));
    },
  },

  async mounted() {
    await this.initializePage();
  },

  methods: {
    async initializePage() {
      try {
        const response = await getCurrentUserProfile();
        if (response.status === "success") {
          this.isAdmin = response.data.tt_admin === true;
        } else {
          this.error = "Failed to load current user.";
        }

        if (this.isAdmin) {
          await this.fetchSearches();
        }
      } catch (error) {
        this.error = "Failed to load monitoring.";
        this.isAdmin = false;
      } finally {
        this.overlay = false;
      }
    },

    async fetchSearches() {
      try {
        this.loadingSearches = true;
        this.error = null;

        const response = await getAllSearches(
          "",
          this.currentPage,
          this.pageSize,
          this.filters
        );
        if (response.status !== "success") {
          throw new Error("Failed to load searches");
        }

        this.searches = (response.data.searches || []).map((search) =>
          withAdminFormattedDates(search, ["run_date"])
        );
        this.expandedSearchRows = [];
        this.totalSearches = response.data.total || 0;
        this.totalPages = response.data.total_pages || 1;
        this.currentPage = response.data.page || this.currentPage;
      } catch (error) {
        this.error = this.getRequestErrorMessage(error, "Failed to load searches. Please try again.");
        this.searches = [];
      } finally {
        this.loadingSearches = false;
      }
    },

    async runSearch() {
      this.currentPage = 1;
      await this.fetchSearches();
    },

    async clearFilters() {
      this.filters = {
        full_name: "",
        email: "",
        plain_text_query: "",
        run_date_from: "",
        run_date_to: "",
      };
      this.currentPage = 1;
      await this.fetchSearches();
    },

    async fetchUserHistory() {
      try {
        this.loadingUserHistory = true;
        this.error = null;

        const response = await getAllUserHistory(
          "",
          this.userHistoryPage,
          this.userHistoryPageSize,
          this.userHistoryFilters
        );
        if (response.status !== "success") {
          throw new Error("Failed to load user history");
        }

        this.userHistory = response.data.history || [];
        this.totalHistory = response.data.total || 0;
        this.totalHistoryPages = response.data.total_pages || 1;
        this.userHistoryPage = response.data.page || this.userHistoryPage;
        this.hasLoadedUserHistory = true;
      } catch (error) {
        this.error = this.getRequestErrorMessage(error, "Failed to load user history. Please try again.");
        this.userHistory = [];
      } finally {
        this.loadingUserHistory = false;
      }
    },

    async runUserHistorySearch() {
      this.userHistoryPage = 1;
      await this.fetchUserHistory();
    },

    async clearUserHistoryFilters() {
      this.userHistoryFilters = {
        full_name: "",
        email: "",
        url: "",
        path: "",
        route_name: "",
        description: "",
        created_from: "",
        created_to: "",
      };
      this.userHistoryPage = 1;
      await this.fetchUserHistory();
    },

    async fetchSearchStats() {
      try {
        this.loadingSearchStats = true;
        this.error = null;

        const response = await getSearchStatsByUser(
          "",
          this.searchStatsPage,
          this.searchStatsPageSize,
          this.searchStatsFilters
        );
        if (response.status !== "success") {
          throw new Error("Failed to load user search stats");
        }

        this.searchStats = response.data.stats || [];
        this.totalSearchStats = response.data.total || 0;
        this.totalSearchStatsPages = response.data.total_pages || 1;
        this.searchStatsPage = response.data.page || this.searchStatsPage;
        this.hasLoadedSearchStats = true;
      } catch (error) {
        this.error = this.getRequestErrorMessage(error, "Failed to load user search stats. Please try again.");
        this.searchStats = [];
      } finally {
        this.loadingSearchStats = false;
      }
    },

    async runSearchStatsSearch() {
      this.searchStatsPage = 1;
      await this.fetchSearchStats();
    },

    async clearSearchStatsFilters() {
      this.searchStatsFilters = {
        full_name: "",
        email: "",
      };
      this.searchStatsPage = 1;
      await this.fetchSearchStats();
    },

    async fetchTraceyThreads() {
      try {
        this.loadingTraceyThreads = true;
        this.error = null;

        const response = await getAllAIConversationThreads(
          "",
          this.traceyThreadsPage,
          this.traceyThreadsPageSize,
          this.traceyThreadFilters
        );
        if (response.status !== "success") {
          throw new Error("Failed to load Tracey threads");
        }

        this.traceyThreads = (response.data.threads || []).map((thread) =>
          withAdminFormattedDates(thread, ["created", "updated"])
        );
        this.totalTraceyThreads = response.data.total || 0;
        this.totalTraceyThreadsPages = response.data.total_pages || 1;
        this.traceyThreadsPage = response.data.page || this.traceyThreadsPage;
        this.hasLoadedTraceyThreads = true;
      } catch (error) {
        this.error = this.getRequestErrorMessage(error, "Failed to load Tracey threads. Please try again.");
        this.traceyThreads = [];
      } finally {
        this.loadingTraceyThreads = false;
      }
    },

    async runTraceyThreadSearch() {
      this.traceyThreadsPage = 1;
      await this.fetchTraceyThreads();
    },

    async clearTraceyThreadFilters() {
      this.traceyThreadFilters = {
        full_name: "",
        email: "",
        title: "",
        status: "",
        created_from: "",
        created_to: "",
        updated_from: "",
        updated_to: "",
      };
      this.traceyThreadsPage = 1;
      await this.fetchTraceyThreads();
    },

    async openThreadDialog(thread) {
      this.selectedThreadId = thread.thread_id;
      this.selectedThreadSummary = withAdminFormattedDates(thread, ["created", "updated"]);
      this.threadContents = [];
      this.threadContentsError = null;
      this.threadContentsPage = 1;
      this.threadDialogOpen = true;
      await this.fetchThreadContents();
    },

    closeThreadDialog() {
      this.threadDialogOpen = false;
    },

    onThreadDialogToggle(isOpen) {
      if (!isOpen) {
        this.selectedThreadId = null;
        this.selectedThreadSummary = {};
        this.threadContents = [];
        this.threadContentsError = null;
        this.threadContentsPage = 1;
        this.totalThreadContents = 0;
        this.totalThreadContentsPages = 1;
      }
    },

    async fetchThreadContents() {
      if (!this.selectedThreadId) {
        return;
      }

      try {
        this.loadingThreadContents = true;
        this.threadContentsError = null;

        const response = await getAIConversationContents(
          this.selectedThreadId,
          this.threadContentsPage,
          this.threadContentsPageSize
        );
        if (response.status !== "success") {
          throw new Error("Failed to load thread contents");
        }

        if (response.data.thread) {
          this.selectedThreadSummary = withAdminFormattedDates(response.data.thread, [
            "created",
            "updated",
          ]);
        }

        this.threadContents = response.data.contents || [];
        this.totalThreadContents = response.data.total || 0;
        this.totalThreadContentsPages = response.data.total_pages || 1;
        this.threadContentsPage = response.data.page || this.threadContentsPage;
      } catch (error) {
        this.threadContentsError = this.getRequestErrorMessage(
          error,
          "Failed to load thread contents. Please try again."
        );
        this.threadContents = [];
      } finally {
        this.loadingThreadContents = false;
      }
    },

    getThreadStatusColor(status) {
      if (status === "active") {
        return "success";
      }

      if (status === "deleted") {
        return "error";
      }

      return "grey";
    },

    getThreadUserInitial(content) {
      const name = content?.full_name || content?.email || "U";
      return String(name).charAt(0).toUpperCase();
    },

    parseAssistantContent(content) {
      if (!content) {
        return "-";
      }

      return parseAssistantMarkdown(content);
    },

    formatDate(value) {
      return formatAdminDateTime(value);
    },

    buildCsvHeaders(tableHeaders) {
      return tableHeaders
        .filter((header) => header.value && header.value !== "actions")
        .reduce((acc, header) => {
          acc[header.value] = header.text || header.value;
          return acc;
        }, {});
    },

    buildCsvFileTitle(prefix, page) {
      const timestamp = new Date().toISOString();
      return `monitoring_${prefix}_page${page}_${timestamp}`;
    },

    buildCsvAllFileTitle(prefix) {
      const timestamp = new Date().toISOString();
      return `monitoring_${prefix}_all_${timestamp}`;
    },

    formatSearchCsvRow(item) {
      return {
        full_name: item.full_name || "",
        email: item.email || "",
        repeats: item.repeats ?? "",
        run_date_display: item.run_date_display || this.formatDate(item.run_date),
        plain_text_query: item.plain_text_query || "",
        is_saved: item.is_saved ? "Saved" : "No",
        alerts_on: item.alerts_on ? "On" : "Off",
      };
    },

    formatUserHistoryCsvRow(item) {
      return {
        full_name: item.full_name || "",
        email: item.email || "",
        created: this.formatDate(item.created),
        route_name: item.route_name || "",
        path: item.path || "",
        description: item.description || "",
        url: item.url || "",
      };
    },

    formatSearchStatsCsvRow(item) {
      return {
        full_name: item.full_name || "",
        email: item.email || "",
        unique_searches: item.unique_searches ?? "",
        num_searches_inc_repeats: item.num_searches_inc_repeats ?? "",
        first_search_date: this.formatDate(item.first_search_date),
        last_new_search_date: this.formatDate(item.last_new_search_date),
        last_search_date: this.formatDate(item.last_search_date),
      };
    },

    formatTraceyThreadCsvRow(item) {
      return {
        full_name: item.full_name || "",
        email: item.email || "",
        title: item.title || "",
        status: item.status || "",
        created_display: item.created_display || this.formatDate(item.created),
        updated_display: item.updated_display || this.formatDate(item.updated),
        thread_id: item.thread_id || "",
      };
    },

    getTraceyThreadExportHeaders() {
      return [
        ...this.traceyThreadHeaders.filter((header) => header.value !== "actions"),
        { text: "Thread ID", value: "thread_id" },
      ];
    },

    exportCurrentPageCsv(tableHeaders, items, fileTitlePrefix, page, formatRow) {
      if (!items.length) {
        return;
      }

      const headers = this.buildCsvHeaders(tableHeaders);
      const rows = items.map((item) => formatRow(item));
      const fileTitle = this.buildCsvFileTitle(fileTitlePrefix, page);

      exportCSVFile(headers, rows, fileTitle);
    },

    triggerCsvDownload(csv, fileTitle) {
      const exportedFilename = `${fileTitle}.csv`;
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

      if (navigator.msSaveBlob) {
        navigator.msSaveBlob(blob, exportedFilename);
        return;
      }

      const link = document.createElement("a");
      if (link.download === undefined) {
        return;
      }

      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", exportedFilename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },

    async downloadAllCsv({
      key,
      label,
      fileTitlePrefix,
      tableHeaders,
      fetchPage,
      extractItems,
      formatRow,
      mapItems,
    }) {
      if (this.downloadingAll) {
        return;
      }

      this.downloadingAll = key;
      this.downloadAllProgress = 0;
      this.downloadAllMessage = `Preparing ${label} download...`;
      this.error = null;

      try {
        const headers = this.buildCsvHeaders(tableHeaders);
        const csvParts = [convertToCSV([headers])];
        const pageSize = 200;
        let page = 1;
        let totalPages = 1;
        let downloadedRows = 0;

        do {
          this.downloadAllMessage = `Downloading ${label} (page ${page} of ${totalPages})...`;
          await this.$nextTick();

          const response = await fetchPage(page, pageSize);
          if (response.status !== "success") {
            throw new Error(`Failed to download ${label}`);
          }

          totalPages = Math.max(response.data.total_pages || 1, 1);
          let items = extractItems(response) || [];
          if (mapItems) {
            items = items.map(mapItems);
          }

          const rows = items.map((item) => formatRow(item));
          if (rows.length) {
            csvParts.push(convertToCSV(rows));
            downloadedRows += rows.length;
          }

          this.downloadAllProgress = Math.min(99, Math.round((page / totalPages) * 100));
          page += 1;
        } while (page <= totalPages);

        if (!downloadedRows) {
          this.error = `No ${label} records available to download.`;
          return;
        }

        this.downloadAllMessage = `Saving ${label} CSV...`;
        this.downloadAllProgress = 100;
        await this.$nextTick();

        this.triggerCsvDownload(csvParts.join(""), this.buildCsvAllFileTitle(fileTitlePrefix));
      } catch (error) {
        this.error = this.getRequestErrorMessage(
          error,
          `Failed to download all ${label}. Please try again.`
        );
      } finally {
        this.downloadingAll = null;
        this.downloadAllMessage = "";
        this.downloadAllProgress = 0;
      }
    },

    downloadSearchesCsv() {
      this.exportCurrentPageCsv(
        this.searchHeaders,
        this.searches,
        "searches",
        this.currentPage,
        (item) => this.formatSearchCsvRow(item)
      );
    },

    downloadAllSearchesCsv() {
      return this.downloadAllCsv({
        key: "searches",
        label: "searches",
        fileTitlePrefix: "searches",
        tableHeaders: this.searchHeaders,
        fetchPage: (page, pageSize) => getAllSearches("", page, pageSize, this.filters),
        extractItems: (response) => response.data.searches,
        formatRow: (item) => this.formatSearchCsvRow(item),
        mapItems: (search) => withAdminFormattedDates(search, ["run_date"]),
      });
    },

    downloadUserHistoryCsv() {
      this.exportCurrentPageCsv(
        this.userHistoryHeaders,
        this.userHistory,
        "user_history",
        this.userHistoryPage,
        (item) => this.formatUserHistoryCsvRow(item)
      );
    },

    downloadAllUserHistoryCsv() {
      return this.downloadAllCsv({
        key: "user_history",
        label: "user history",
        fileTitlePrefix: "user_history",
        tableHeaders: this.userHistoryHeaders,
        fetchPage: (page, pageSize) =>
          getAllUserHistory("", page, pageSize, this.userHistoryFilters),
        extractItems: (response) => response.data.history,
        formatRow: (item) => this.formatUserHistoryCsvRow(item),
      });
    },

    downloadSearchStatsCsv() {
      this.exportCurrentPageCsv(
        this.searchStatsHeaders,
        this.searchStats,
        "search_stats",
        this.searchStatsPage,
        (item) => this.formatSearchStatsCsvRow(item)
      );
    },

    downloadAllSearchStatsCsv() {
      return this.downloadAllCsv({
        key: "search_stats",
        label: "user search stats",
        fileTitlePrefix: "search_stats",
        tableHeaders: this.searchStatsHeaders,
        fetchPage: (page, pageSize) =>
          getSearchStatsByUser("", page, pageSize, this.searchStatsFilters),
        extractItems: (response) => response.data.stats,
        formatRow: (item) => this.formatSearchStatsCsvRow(item),
      });
    },

    downloadTraceyThreadsCsv() {
      this.exportCurrentPageCsv(
        this.getTraceyThreadExportHeaders(),
        this.traceyThreads,
        "tracey_threads",
        this.traceyThreadsPage,
        (item) => this.formatTraceyThreadCsvRow(item)
      );
    },

    downloadAllTraceyThreadsCsv() {
      return this.downloadAllCsv({
        key: "tracey_threads",
        label: "Tracey threads",
        fileTitlePrefix: "tracey_threads",
        tableHeaders: this.getTraceyThreadExportHeaders(),
        fetchPage: (page, pageSize) =>
          getAllAIConversationThreads("", page, pageSize, this.traceyThreadFilters),
        extractItems: (response) => response.data.threads,
        formatRow: (item) => this.formatTraceyThreadCsvRow(item),
        mapItems: (thread) => withAdminFormattedDates(thread, ["created", "updated"]),
      });
    },

    shortenText(text, maxLength = 120) {
      if (!text) {
        return "";
      }

      const normalized = String(text).replace(/(<([^>]+)>)/gi, "");
      if (normalized.length <= maxLength) {
        return normalized;
      }

      return `${normalized.slice(0, maxLength).trim()}...`;
    },

    formatQueryStringParams(params) {
      if (!params || typeof params !== "object") {
        return "-";
      }

      const entries = Object.entries(params);
      if (!entries.length) {
        return "-";
      }

      return entries
        .map(([key, value]) => {
          const normalizedValue = Array.isArray(value) ? value.join(", ") : value;
          return `${key}: ${normalizedValue}`;
        })
        .join(" | ");
    },

    getHistoryDescriptionTitle(item) {
      return item?.short_description || item?.route_name || item?.description || "-";
    },

    getHistoryDescriptionSubtitle(item) {
      const title = this.getHistoryDescriptionTitle(item);
      const description = item?.description || "";

      if (!description || description === title) {
        return "";
      }

      return description;
    },

    getSearchQueryTitle(item) {
      return item?.search_query?.search_name || "Plain text query";
    },

    getSearchQuerySubtitle(item) {
      return item?.plain_text_query || "";
    },

    toggleSearchExpansion(item) {
      const existingIndex = this.expandedSearchRows.findIndex(
        (expandedItem) => expandedItem.id === item.id
      );

      if (existingIndex >= 0) {
        this.expandedSearchRows = this.expandedSearchRows.filter(
          (expandedItem) => expandedItem.id !== item.id
        );
        return;
      }

      this.expandedSearchRows = [item];
    },

    getRequestErrorMessage(error, fallbackMessage) {
      const responseData = error?.response?.data;

      if (typeof responseData === "string" && responseData.trim()) {
        return responseData;
      }

      if (responseData?.message) {
        return responseData.message;
      }

      if (responseData?.error) {
        return responseData.error;
      }

      if (responseData?.errors && Array.isArray(responseData.errors) && responseData.errors.length > 0) {
        return responseData.errors.join(", ");
      }

      if (error?.message) {
        return error.message;
      }

      return fallbackMessage;
    },
  },

  watch: {
    async activeTab(value) {
      if (value === 1 && !this.hasLoadedUserHistory && !this.loadingUserHistory) {
        await this.fetchUserHistory();
      }
      if (value === 2 && !this.hasLoadedSearchStats && !this.loadingSearchStats) {
        await this.fetchSearchStats();
      }
      if (value === 3 && !this.hasLoadedTraceyThreads && !this.loadingTraceyThreads) {
        await this.fetchTraceyThreads();
      }
    },
  },
};
</script>

<style scoped>
.monitoring-administration {
  background-color: #f5f5f5;
}

.monitoring-link {
  color: #1976d2;
  text-decoration: none;
}

.monitoring-link:hover {
  text-decoration: underline;
}

.monitoring-query-button {
  background: transparent;
  border: 0;
  cursor: pointer;
  display: block;
  padding: 0;
  text-align: left;
  width: 100%;
}

.monitoring-query-button__title {
  color: #1976d2;
  display: block;
  font-weight: 500;
  line-height: 1.25;
}

.monitoring-query-button__subtitle {
  color: rgba(0, 0, 0, 0.6);
  display: block;
  font-size: 0.875rem;
  line-height: 1.3;
  margin-top: 2px;
}

.monitoring-description__title {
  font-weight: 500;
  line-height: 1.25;
}

.monitoring-description__subtitle {
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.875rem;
  line-height: 1.3;
  margin-top: 2px;
}

.transparent-tabs {
  background: transparent;
}

.thread-messages {
  margin: 0 -16px;
}

.ai-turn {
  display: flex;
  gap: 16px;
  padding: 20px 24px;
  width: 100%;
}

.ai-turn--user {
  background-color: transparent;
}

.ai-turn--assistant {
  background-color: #fff;
  border-top: 1px solid #ececf1;
  border-bottom: 1px solid #ececf1;
}

.ai-turn__avatar {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e3e3e8;
  color: #353740;
  font-size: 13px;
  font-weight: 600;
}

.ai-turn__avatar--tracey {
  background: linear-gradient(135deg, #12253a 0%, #1e3a5f 100%);
}

.ai-turn__avatar-letter {
  line-height: 1;
}

.ai-turn__body {
  flex: 1;
  min-width: 0;
  max-width: 100%;
}

.ai-turn__meta {
  font-size: 11px;
  color: #8e8ea0;
  margin-bottom: 6px;
}

.ai-turn__text {
  font-size: 15px;
  line-height: 1.65;
  color: #353740;
  word-wrap: break-word;
}

.ai-turn--user .ai-turn__text {
  color: #0d0d0d;
  max-width: 56rem;
}

.thread-message-tools {
  margin-top: 12px;
  max-width: 56rem;
}

.thread-message-tools__label {
  color: #8e8ea0;
  font-size: 11px;
  margin-bottom: 6px;
}

.ai-prose ::v-deep p,
.ai-prose ::v-deep ul,
.ai-prose ::v-deep ol,
.ai-prose ::v-deep blockquote,
.ai-prose ::v-deep h1,
.ai-prose ::v-deep h2,
.ai-prose ::v-deep h3,
.ai-prose ::v-deep h4,
.ai-prose ::v-deep h5,
.ai-prose ::v-deep h6 {
  max-width: 56rem;
}

.ai-prose ::v-deep p {
  margin: 0 0 0.75em;
}

.ai-prose ::v-deep p:last-child {
  margin-bottom: 0;
}

.ai-prose ::v-deep a {
  color: #ff1463;
  text-decoration: underline;
}

.ai-prose ::v-deep img {
  display: block;
  max-width: 100%;
  height: auto;
  margin: 0.75em 0;
  border-radius: 8px;
}

.ai-prose ::v-deep pre,
.ai-prose ::v-deep code {
  max-width: 100%;
}

.ai-prose ::v-deep pre {
  overflow-x: auto;
  padding: 12px;
  border-radius: 8px;
  background: #f7f7f8;
}

.ai-prose ::v-deep .ai-prose__table-wrap {
  overflow-x: auto;
  margin: 0.75em 0;
  max-width: 100%;
  width: 100%;
}

.ai-prose ::v-deep table {
  border-collapse: collapse;
  width: max-content;
  min-width: 100%;
  font-size: 13px;
}

.ai-prose ::v-deep th,
.ai-prose ::v-deep td {
  padding: 8px 12px;
  border: 1px solid #ececf1;
  text-align: left;
  max-width: none;
  white-space: normal;
}

.ai-prose ::v-deep th {
  background: #f7f7f8;
}
</style>
